/**
 * Taking a page's photographs away whole: the stored originals, in the page's
 * order, in one zip.
 *
 * The image service only ever hands out renders — every URL it signs carries
 * at least one edit — so the original is read from the store itself. The
 * service URL names the stored key right after its bucket segment,
 * `/images/s1/<key>/<edits>`, and the store serves that key at `/assets/<key>`.
 * A CDN wrapper in front (`/images/cdn-images/<params>/images/s1/…`) does not
 * change that: the key is still the path after `/images/s1/`.
 */

const SERVICE_KEY =
    /\/images\/s1\/(.+?\.(?:jpe?g|png|webp|avif|gif|tiff?|heic))(?=\/|\?|$)/i;

/** The stored key behind an image URL, or null where it is not a stored image. */
export const storedImageKey = (src: string): string | null => {
    const match = SERVICE_KEY.exec(src);
    if (!match?.[1]) return null;
    try {
        return decodeURIComponent(match[1]);
    } catch {
        return match[1];
    }
};

/** The original upload behind an image URL, or the URL itself if it has none. */
export const originalImageUrl = (src: string): string => {
    const key = storedImageKey(src);
    return key ? `/assets/${key}` : src;
};

/**
 * The page's photographs as originals, once each, in the order given. The
 * same file often arrives several times over under different crops.
 */
export const uniqueOriginals = (sources: (string | null | undefined)[]) => {
    const seen = new Set<string>();
    const originals: string[] = [];
    for (const src of sources) {
        if (!src) continue;
        const url = originalImageUrl(src);
        const id = storedImageKey(src) ?? url;
        if (seen.has(id)) continue;
        seen.add(id);
        originals.push(url);
    }
    return originals;
};

/**
 * The name a photograph takes in the zip: its place on the page, then the
 * file's own name, so the folder sorts the way the page reads.
 */
export const zipEntryName = (url: string, index: number, total: number) => {
    const base = decodeURIComponent(
        url.split('?')[0]?.split('/').pop() || 'image',
    );
    const width = String(total).length;
    return `${String(index + 1).padStart(width, '0')}-${base}`;
};

/** A title as a file name: lower case, words joined, nothing a filesystem minds. */
export const zipFileName = (title: string) => {
    const slug = title
        .toLowerCase()
        .normalize('NFKD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80);
    return `${slug || 'archipro'}-images.zip`;
};

interface DownloadProgress {
    done: number;
    total: number;
}

/**
 * Fetches each original and hands the browser one zip. Files are stored rather
 * than recompressed: photographs are already compressed, and deflating them
 * again only costs time. A file that will not load is left out rather than
 * failing the rest.
 */
export const downloadImagesZip = async (
    urls: string[],
    title: string,
    onProgress?: (progress: DownloadProgress) => void,
): Promise<{ saved: number; failed: number }> => {
    const { default: JSZip } = await import('jszip');
    const zip = new JSZip();
    let done = 0;
    let failed = 0;
    onProgress?.({ done, total: urls.length });

    await Promise.all(
        urls.map(async (url, index) => {
            try {
                const response = await fetch(url, { credentials: 'include' });
                if (!response.ok) throw new Error(String(response.status));
                zip.file(
                    zipEntryName(url, index, urls.length),
                    await response.blob(),
                );
            } catch {
                failed += 1;
            } finally {
                done += 1;
                onProgress?.({ done, total: urls.length });
            }
        }),
    );

    const saved = urls.length - failed;
    if (saved === 0) return { saved, failed };

    const blob = await zip.generateAsync({
        type: 'blob',
        compression: 'STORE',
    });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = zipFileName(title);
    document.body.appendChild(link);
    link.click();
    link.remove();
    // The click has handed the blob to the download; the URL can go once the
    // browser has read it.
    setTimeout(() => URL.revokeObjectURL(href), 10_000);
    return { saved, failed };
};
